// frontend/src/utils/clipboard.js
/**
 * Clipboard utilities with a non-secure-context fallback.
 *
 * Why this exists:
 * The async Clipboard API (`navigator.clipboard.writeText`) is ONLY available
 * in a secure context — HTTPS or `http://localhost`. When the HR Portal is
 * reached over the LAN by IP/hostname on plain HTTP (a common internal-tool
 * setup), `navigator.clipboard` is `undefined`, so a direct call throws and
 * every "Copy" button silently fails. That is especially dangerous on the
 * one-time secret reveals (Service API keys, App tokens) where the value can
 * never be shown again.
 *
 * `copyToClipboard` tries the modern API first, then falls back to the legacy
 * `document.execCommand('copy')` via an off-screen textarea so copying works
 * regardless of transport. Callers get a boolean and own their own toast/UX.
 */

/**
 * Legacy copy path for non-secure contexts (HTTP over LAN, older browsers).
 * Uses a hidden, focus-preserving textarea + `document.execCommand('copy')`.
 *
 * @param {string} text
 * @returns {boolean} whether the copy command reported success
 */
const copyViaExecCommand = (text) => {
  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Keep it out of view and prevent the page from scrolling/zooming to it.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';

  // Preserve focus (e.g. a modal/button) so the UI doesn't jump.
  const previouslyFocused = document.activeElement;

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  // iOS Safari needs an explicit range selection to copy.
  textarea.setSelectionRange(0, text.length);

  let succeeded = false;
  try {
    succeeded = document.execCommand('copy');
  } catch {
    succeeded = false;
  }

  document.body.removeChild(textarea);
  if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
    previouslyFocused.focus();
  }

  return succeeded;
};

/**
 * Copy text to the clipboard, working in both secure and non-secure contexts.
 *
 * @param {string} text - The text to copy.
 * @returns {Promise<boolean>} `true` if the copy succeeded, `false` otherwise.
 */
export const copyToClipboard = async (text) => {
  if (text == null) return false;
  const value = String(text);

  // Preferred path: async Clipboard API (requires a secure context).
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function' &&
    (typeof window === 'undefined' || window.isSecureContext !== false)
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Fall through to the legacy path (permissions denied, non-secure, etc.).
    }
  }

  return copyViaExecCommand(value);
};

export default { copyToClipboard };
